import {
  badgeState,
  courseFieldArt,
  courseNumberInField,
} from "@/lib/badges/course-badges";
import { FIELDS, fieldOfCourseSlug } from "@/lib/badges/course-fields";

/**
 * このコースで取れるバッジ。
 *
 * 終えると何がもらえるのかを、終える前に見せる。取ったあとにしか出さないと、
 * 取りに行く理由にならない。
 *
 * 絵は分野ごとに 1 枚しかなく、同じ分野のコースは右下の番号で見分ける。
 */
export function CourseBadgePanel({
  courseSlug,
  completedLessons,
  totalLessons,
}: {
  courseSlug: string;
  completedLessons: number;
  totalLessons: number;
}) {
  const field = fieldOfCourseSlug(courseSlug);
  if (!field) return null;
  const fieldTitle = FIELDS.find((f) => f.slug === field)?.title;
  const number = courseNumberInField(courseSlug);
  if (!fieldTitle || number === 0) return null;

  const state = badgeState(completedLessons, totalLessons);
  const done = state === "done";

  return (
    <section className="px-4 md:px-0 max-w-4xl mx-auto w-full mt-8">
      <div className="flex items-center gap-5 bg-surface border border-main rounded-xl p-5 md:p-6">
        <img
          src={courseFieldArt(field, state, number)}
          alt={
            done
              ? `${fieldTitle} 第${number}コースのバッジ 獲得済み`
              : `${fieldTitle} 第${number}コースのバッジ 未獲得`
          }
          width={128}
          height={128}
          loading="lazy"
          // 小さくすると絵の中の細部が潰れる。外側の淡い輪が幅の 3 割を
          // 取るので、見かけより実際の絵は小さい
          className="size-24 md:size-32 shrink-0 object-contain"
        />
        <div className="min-w-0">
          <p className="font-noto text-[15px] md:text-sm text-sub mb-1">
            {done ? "獲得したバッジ" : "このコースを終えると取れるバッジ"}
          </p>
          <p className="font-noto font-bold text-base md:text-lg text-main">
            {fieldTitle} <span className="whitespace-nowrap">第{number}コース</span>
          </p>
          {/* まだ 1 本も済んでいない人には「0 レッスン済み」を出さない */}
          {totalLessons > 0 && completedLessons === 0 ? null : (
            <p className="font-noto text-[15px] md:text-sm text-sub mt-1 tabular-nums">
              {totalLessons > 0
                ? `${completedLessons} レッスン済み`
                : "レッスンの準備中です"}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
